import React, { useState } from "react";
import AnalyticsToggle from "./analytics-toggle";
import AnalyticsWrapper from "./analytics-wrapper";
import PageLoadingState from "./page-loading-state";
import StatisticsCard from "./statistics-card";
import MobilePageLayout, { MobilePageLayoutProps } from "./mobile/mobile-page-layout";
import MobilePageSkeleton from "./mobile/mobile-page-skeleton";
import { StatItem } from "./mobile/types";
import { useIsMobile } from "../hooks/use-is-mobile";

export type { StatItem };

type MobileOptions = Pick<
  MobilePageLayoutProps,
  | "onRefresh"
  | "primaryAction"
  | "actions"
  | "filters"
  | "activeFilterCount"
  | "onResetFilters"
  | "headerContent"
  | "backTo"
  | "onBack"
>;

interface PageShellProps {
  title: string;
  description?: React.ReactNode;
  loading?: boolean;
  /** Desktop header controls (filters, refresh, primary buttons). */
  toolbar?: React.ReactNode;
  stats?: StatItem[];
  statsColumns?: 2 | 3 | 4 | 5 | 6;
  /** Charts shown under the stats; collapsed into "Insights" on phones. */
  insights?: React.ReactNode;
  mobile?: MobileOptions;
  children: React.ReactNode;
}

const STATS_GRID_COLUMNS: Record<NonNullable<PageShellProps["statsColumns"]>, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
  5: "md:grid-cols-5",
  6: "md:grid-cols-6",
};

const DesktopPageShell: React.FC<PageShellProps> = ({
  title,
  description,
  loading,
  toolbar,
  stats,
  statsColumns = 3,
  insights,
  children,
}) => {
  const [showAnalytics, setShowAnalytics] = useState(true);
  const hasAnalytics = (stats && stats.length > 0) || !!insights;

  if (loading) return <PageLoadingState showAnalytics={hasAnalytics && showAnalytics} />;

  return (
    <div className="h-full w-full bg-gray-50 overflow-hidden flex flex-col">
      <div className="flex-grow overflow-auto p-6 m-3 bg-background text-foreground border border-gray-100 rounded-xl">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="mr-auto">
            <h1 className="text-2xl font-bold font-jakarta">{title}</h1>
            {description && <p className="text-gray-800 text-sm mt-1">{description}</p>}
          </div>
          {toolbar && <div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
        </div>
        {hasAnalytics && (
          <AnalyticsToggle showAnalytics={showAnalytics} setShowAnalytics={setShowAnalytics} />
        )}
        <div className="space-y-6">
          {hasAnalytics && (
            <AnalyticsWrapper showAnalytics={showAnalytics}>
              {stats && stats.length > 0 && (
                <div className={`grid grid-cols-1 ${STATS_GRID_COLUMNS[statsColumns]} gap-4`}>
                  {stats.map((item) => (
                    <StatisticsCard
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      icon={item.icon}
                      description={item.description}
                    />
                  ))}
                </div>
              )}
              {insights}
            </AnalyticsWrapper>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

const PageShell: React.FC<PageShellProps> = (props) => {
  const isMobile = useIsMobile();

  if (!isMobile) return <DesktopPageShell {...props} />;
  if (props.loading) return <MobilePageSkeleton />;

  return (
    <MobilePageLayout
      title={props.title}
      description={props.description}
      stats={props.stats}
      insights={props.insights}
      {...props.mobile}
    >
      {props.children}
    </MobilePageLayout>
  );
};

export default PageShell;
